import type {
  AnalyticsSummaryResponse,
  BrandingSettings,
  BrandingTheme,
  CustomDomain,
  CustomDomainsResponse,
  ExportJob,
  ExportJobResponse,
  ExportJobsResponse,
  FormDetail,
  FormSchemaV1,
  FormShare,
  FormSummary,
  FormVersionDetail,
  ListFormsResponse,
  SessionResponse,
  TeamRoleGrant,
  ThemeListResponse,
  User,
  UserDetailResponse,
  UsersListResponse,
  TeamSummary,
  TeamMember,
  TeamInvitation,
  FormSharesResponse,
} from "../types/api";

const API_BASE_URL =
  (typeof __API_BASE_URL__ !== "undefined" && __API_BASE_URL__) ||
  import.meta.env.VITE_API_BASE_URL ||
  "";

interface RequestInitExtras extends RequestInit {
  skipAuth?: boolean;
}

async function request<T>(path: string, init?: RequestInitExtras): Promise<T> {
  const response = await fetch(buildUrl(path), {
    credentials: init?.skipAuth ? "omit" : "include",
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {}),
    },
    ...init,
  });

  if (!response.ok) {
    const errorBody = await safeJson(response);
    const message =
      (errorBody && typeof errorBody.error === "string" ? errorBody.error : null) ??
      `Request failed with status ${response.status}`;
    const error = new Error(message) as Error & { status?: number; details?: unknown };
    error.status = response.status;
    if (errorBody && "details" in errorBody) {
      error.details = (errorBody as Record<string, unknown>).details;
    }
    throw error;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

function buildUrl(path: string): string {
  if (API_BASE_URL) {
    return `${API_BASE_URL}${path}`;
  }
  return path;
}

async function safeJson(response: Response): Promise<Record<string, unknown> | null> {
  try {
    const data = (await response.json()) as unknown;
    if (typeof data === "object" && data !== null) {
      return data as Record<string, unknown>;
    }
    return null;
  } catch {
    return null;
  }
}

export async function fetchSession(): Promise<SessionResponse> {
  return request<SessionResponse>("/auth/session", { method: "GET" });
}

export async function fetchForms(params?: {
  teamId?: string;
  search?: string;
  cursor?: string;
  limit?: number;
}): Promise<ListFormsResponse> {
  const usp = new URLSearchParams();
  if (params?.teamId) usp.set("teamId", params.teamId);
  if (params?.search) usp.set("search", params.search);
  if (params?.cursor) usp.set("cursor", params.cursor);
  if (params?.limit) usp.set("limit", params.limit.toString());

  const query = usp.toString();
  return request<ListFormsResponse>(`/forms${query ? `?${query}` : ""}`, {
    method: "GET",
  });
}

export async function fetchFormDetail(formId: string): Promise<FormDetail> {
  return request<FormDetail>(`/forms/${formId}`, { method: "GET" });
}

export async function createForm(payload: {
  teamId: string;
  title: string;
  description?: string | null;
}): Promise<FormDetail> {
  return request<FormDetail>("/forms", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateFormMetadata(
  formId: string,
  payload: Partial<Pick<FormSummary, "title" | "description">> & { settings?: Record<string, unknown> | null }
): Promise<{ form: FormSummary }> {
  return request<{ form: FormSummary }>(`/forms/${formId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function createDraftVersion(formId: string, schema: FormSchemaV1): Promise<FormVersionDetail> {
  return request<FormVersionDetail>(`/forms/${formId}/versions`, {
    method: "POST",
    body: JSON.stringify({ schema }),
  });
}

export async function updateDraftVersion(
  versionId: string,
  schema: FormSchemaV1
): Promise<FormVersionDetail> {
  return request<FormVersionDetail>(`/form-versions/${versionId}/schema`, {
    method: "PUT",
    body: JSON.stringify({ schema }),
  });
}

export async function publishDraftVersion(versionId: string): Promise<FormVersionDetail> {
  return request<FormVersionDetail>(`/form-versions/${versionId}/publish`, {
    method: "POST",
  });
}

export async function fetchFormVersion(versionId: string): Promise<FormVersionDetail> {
  return request<FormVersionDetail>(`/form-versions/${versionId}`, {
    method: "GET",
  });
}

/**
 * Fetch form version via share token (public endpoint, no auth required)
 */
export async function fetchFormVersionByShareToken(token: string): Promise<FormVersionDetail> {
  return request<FormVersionDetail>(`/shares/${token}/form-version`, {
    method: "GET",
    skipAuth: true,
  });
}

export async function createFileUploadSession(
  formId: string,
  payload: { filename: string; contentType: string; sizeBytes: number }
): Promise<{ uploadId: string; key: string }> {
  return request<{ uploadId: string; key: string }>(`/forms/${formId}/uploads`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function uploadFilePart(
  formId: string,
  uploadId: string,
  partNumber: number,
  payload: { key: string; data: string } // data is base64 encoded
): Promise<{ partNumber: number; etag: string }> {
  return request<{ partNumber: number; etag: string }>(
    `/forms/${formId}/uploads/${uploadId}/parts/${partNumber}`,
    {
      method: "POST",
      body: JSON.stringify({ partNumber, key: payload.key, data: payload.data }),
    }
  );
}

export async function completeFileUpload(
  formId: string,
  uploadId: string,
  payload: { key: string; filename: string; contentType: string; sizeBytes: number; parts: Array<{ partNumber: number; etag: string }> }
): Promise<{ key: string; success: boolean }> {
  return request<{ key: string; success: boolean }>(`/forms/${formId}/uploads/${uploadId}/complete`, {
    method: "POST",
    body: JSON.stringify({
      key: payload.key,
      filename: payload.filename,
      contentType: payload.contentType,
      sizeBytes: payload.sizeBytes,
      parts: payload.parts,
    }),
  });
}

export async function listFormUploads(formId: string): Promise<{ uploads: import("../types/api").FormUpload[] }> {
  return request<{ uploads: import("../types/api").FormUpload[] }>(`/forms/${formId}/uploads`, { method: "GET" });
}

export async function deleteFormUpload(formId: string, uploadId: string): Promise<{ success: boolean; deletedId: string }> {
  return request<{ success: boolean; deletedId: string }>(`/forms/${formId}/uploads/${uploadId}`, { method: "DELETE" });
}

// ==================== Public File Upload (for share page) ====================

export async function createPublicFileUploadSession(
  formId: string,
  shareToken: string,
  payload: { filename: string; contentType: string; sizeBytes: number }
): Promise<{ uploadId: string; key: string }> {
  return request<{ uploadId: string; key: string }>(`/forms/${formId}/public-uploads`, {
    method: "POST",
    body: JSON.stringify({ ...payload, shareToken }),
    skipAuth: true,
  });
}

export async function uploadPublicFilePart(
  formId: string,
  uploadId: string,
  partNumber: number,
  shareToken: string,
  payload: { key: string; data: string }
): Promise<{ partNumber: number; etag: string }> {
  return request<{ partNumber: number; etag: string }>(
    `/forms/${formId}/public-uploads/${uploadId}/parts/${partNumber}`,
    {
      method: "POST",
      body: JSON.stringify({ partNumber, key: payload.key, data: payload.data, shareToken }),
      skipAuth: true,
    }
  );
}

export async function completePublicFileUpload(
  formId: string,
  uploadId: string,
  shareToken: string,
  payload: { key: string; parts: Array<{ partNumber: number; etag: string }> }
): Promise<{ key: string; success: boolean }> {
  return request<{ key: string; success: boolean }>(
    `/forms/${formId}/public-uploads/${uploadId}/complete`,
    {
      method: "POST",
      body: JSON.stringify({ key: payload.key, parts: payload.parts, shareToken }),
      skipAuth: true,
    }
  );
}

/**
 * Direct binary upload for small files (< 5MB) - single request, no base64 encoding
 * Much faster than multipart upload for small files
 */
export async function directPublicUpload(
  formId: string,
  shareToken: string,
  file: File
): Promise<{ key: string; success: boolean }> {
  const API_BASE =
    (typeof __API_BASE_URL__ !== "undefined" && __API_BASE_URL__) ||
    import.meta.env.VITE_API_BASE_URL ||
    "";

  const response = await fetch(
    `${API_BASE}/forms/${formId}/public-uploads/direct?shareToken=${encodeURIComponent(shareToken)}&filename=${encodeURIComponent(file.name)}&contentType=${encodeURIComponent(file.type || "application/octet-stream")}`,
    {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": file.type || "application/octet-stream",
      },
    }
  );

  if (!response.ok) {
    const errorBody = await response.json().catch(() => null);
    throw new Error(errorBody?.error || `Upload failed with status ${response.status}`);
  }

  return response.json();
}


export async function createPreviewToken(versionId: string): Promise<{ token: string; expiresAt: string }> {
  return request<{ token: string; expiresAt: string }>(`/form-versions/${versionId}/preview-token`, {
    method: "POST",
  });
}

export async function fetchTeams(): Promise<{ teams: TeamSummary[] }> {
  return request<{ teams: TeamSummary[] }>("/teams", { method: "GET" });
}

export async function createTeam(payload: {
  name: string;
  slug?: string | null;
  planTier?: string | null;
}): Promise<{ team: TeamSummary }> {
  return request<{ team: TeamSummary }>("/teams", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function fetchTeamMembers(teamId: string): Promise<{ members: TeamMember[] }> {
  return request<{ members: TeamMember[] }>(`/teams/${teamId}/members`, { method: "GET" });
}

export async function fetchTeamInvitations(teamId: string): Promise<{ invitations: TeamInvitation[] }> {
  return request<{ invitations: TeamInvitation[] }>(`/teams/${teamId}/invitations`, { method: "GET" });
}

export async function createTeamInvitationRequest(
  teamId: string,
  payload: { email: string; role: string; expiresInDays?: number }
): Promise<{ invitation: TeamInvitation }> {
  return request<{ invitation: TeamInvitation }>(`/teams/${teamId}/invitations`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function fetchTeamRoles(teamId: string): Promise<{ roles: TeamRoleGrant[] }> {
  return request<{ roles: TeamRoleGrant[] }>(`/teams/${teamId}/roles`, { method: "GET" });
}

export async function grantTeamRoleRequest(
  teamId: string,
  payload: {
    subjectType: "user" | "service_account" | "email_domain";
    subjectId: string;
    role: string;
    expiresAt?: string | null;
    metadata?: Record<string, unknown> | null;
  }
): Promise<{ role: TeamRoleGrant }> {
  return request<{ role: TeamRoleGrant }>(`/teams/${teamId}/roles`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function revokeTeamRole(teamId: string, roleId: string): Promise<void> {
  await request(`/teams/${teamId}/roles/${roleId}`, { method: "DELETE" });
}

export async function fetchAnalyticsSummary(
  formId: string,
  params?: { days?: number }
): Promise<AnalyticsSummaryResponse> {
  const usp = new URLSearchParams();
  if (params?.days) {
    usp.set("days", params.days.toString());
  }
  const query = usp.toString();
  const path = `/analytics/forms/${formId}/summary${query ? `?${query}` : ""}`;
  return request<AnalyticsSummaryResponse>(path, { method: "GET" });
}

export async function listExportJobs(formId: string): Promise<ExportJobsResponse> {
  return request<ExportJobsResponse>(`/forms/${formId}/exports`, { method: "GET" });
}

export async function createExportJobRequest(
  formId: string,
  payload: {
    format: ExportJob["format"];
    formVersionIds?: string[];
    filters?: Record<string, unknown> | null;
    notifyEmail?: string | null;
    expiresInDays?: number | null;
  }
): Promise<ExportJobResponse> {
  return request<ExportJobResponse>(`/forms/${formId}/exports`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function fetchExportJob(jobId: string): Promise<ExportJobResponse> {
  return request<ExportJobResponse>(`/exports/${jobId}`, { method: "GET" });
}

export async function listFormShares(
  formId: string,
  params?: { cursor?: string; limit?: number }
): Promise<FormSharesResponse> {
  const usp = new URLSearchParams();
  if (params?.cursor) usp.set("cursor", params.cursor);
  if (params?.limit) usp.set("limit", params.limit.toString());
  const query = usp.toString();
  return request<FormSharesResponse>(`/forms/${formId}/shares${query ? `?${query}` : ""}`, { method: "GET" });
}

export async function createFormShareRequest(
  formId: string,
  payload: {
    shareType: FormShare["shareType"];
    formVersionId?: string | null;
    password?: string | null;
    expiresAt?: string | null;
    expiresInHours?: number | null;
    maxUses?: number | null;
    metadata?: Record<string, unknown> | null;
    domainId?: string | null;
  }
): Promise<{ share: FormShare }> {
  return request<{ share: FormShare }>(`/forms/${formId}/shares`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function deleteFormShareRequest(formId: string, shareId: string): Promise<void> {
  await request(`/forms/${formId}/shares/${shareId}`, { method: "DELETE" });
}

export async function updateFormShareRequest(
  formId: string,
  shareId: string,
  payload: {
    formVersionId?: string | null;
    expiresAt?: string | null;
    maxUses?: number | null;
    metadata?: Record<string, unknown> | null;
    domainId?: string | null;
  }
): Promise<{ share: FormShare }> {
  return request<{ share: FormShare }>(`/forms/${formId}/shares/${shareId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export interface ShareResolveResponse {
  share: {
    formId: string;
    formVersionId: string | null;
    shareType: "public" | "password" | "one_time" | "embed";
    requiresPassword: boolean;
    expiresAt: string | null;
    maxUses: number | null;
    usedCount: number;
    metadata: Record<string, unknown> | null;
  };
}

export interface ShareConsumeResponse {
  share: {
    formId: string;
    formVersionId: string | null;
    shareType: "public" | "password" | "one_time" | "embed";
    metadata: Record<string, unknown> | null;
  };
}

export async function resolveShare(token: string): Promise<ShareResolveResponse> {
  return request<ShareResolveResponse>(`/shares/${token}`, {
    method: "GET",
    skipAuth: true,
  });
}

export async function consumeShare(token: string, password?: string | null): Promise<ShareConsumeResponse> {
  return request<ShareConsumeResponse>(`/shares/${token}/consume`, {
    method: "POST",
    body: JSON.stringify({ password: password ?? null }),
    skipAuth: true,
  });
}

export interface FormSubmissionResponse {
  responseId: string;
  submittedAt: string;
  success: boolean;
}

export interface FormSubmissionData {
  shareToken?: string;
  formVersionId: string;
  responseData: Record<string, unknown>;
  files?: Array<{
    fieldId: string;
    fileName: string;
    contentType: string;
    sizeBytes: number;
    tempKey: string;
  }>;
  metadata?: {
    duration?: number;
  };
}

export async function submitFormResponse(
  formId: string,
  data: FormSubmissionData
): Promise<FormSubmissionResponse> {
  return request<FormSubmissionResponse>(`/forms/${formId}/responses`, {
    method: "POST",
    body: JSON.stringify(data),
    skipAuth: true, // Public form submissions don't require auth
  });
}

export function buildApiUrl(path: string): string {
  return buildUrl(path);
}

export function getApiBaseUrl(): string {
  return API_BASE_URL;
}


export async function fetchTeamThemes(teamId: string): Promise<ThemeListResponse> {
  return request<ThemeListResponse>(`/teams/${teamId}/themes`, { method: "GET" });
}

export async function createTheme(
  teamId: string,
  payload: { name: string; settings?: Partial<BrandingSettings> | null; setDefault?: boolean }
): Promise<{ theme: BrandingTheme }> {
  return request<{ theme: BrandingTheme }>(`/teams/${teamId}/themes`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateTheme(
  teamId: string,
  themeId: string,
  payload: { name?: string; settings?: Partial<BrandingSettings> | null; setDefault?: boolean }
): Promise<{ theme: BrandingTheme }> {
  return request<{ theme: BrandingTheme }>(`/teams/${teamId}/themes/${themeId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteTheme(teamId: string, themeId: string): Promise<void> {
  await request(`/teams/${teamId}/themes/${themeId}`, { method: "DELETE" });
}

export async function setFormTheme(formId: string, payload: { themeId: string | null }): Promise<void> {
  await request(`/forms/${formId}/theme`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

// GDPR endpoints
export async function requestDataExport(): Promise<Blob> {
  const response = await fetch(buildUrl("/gdpr/data-export"), {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
    },
  });

  if (!response.ok) {
    const errorBody = await safeJson(response);
    const message =
      (errorBody && typeof errorBody.error === "string" ? errorBody.error : null) ??
      `Request failed with status ${response.status}`;
    throw new Error(message);
  }

  return response.blob();
}

export async function requestAccountDeletion(confirmation: string): Promise<{ success: boolean; message: string; deletedAt: string }> {
  return request<{ success: boolean; message: string; deletedAt: string }>("/gdpr/data-delete", {
    method: "POST",
    body: JSON.stringify({ confirmation }),
  });
}

export async function getConsentStatus(): Promise<{ userId: string; consent: { analytics_consent: boolean; marketing_consent: boolean } }> {
  return request<{ userId: string; consent: { analytics_consent: boolean; marketing_consent: boolean } }>("/gdpr/consent", {
    method: "GET",
  });
}

export async function updateConsent(analyticsConsent: boolean, marketingConsent: boolean): Promise<{ success: boolean; consent: { analyticsConsent: boolean; marketingConsent: boolean } }> {
  return request<{ success: boolean; consent: { analyticsConsent: boolean; marketingConsent: boolean } }>("/gdpr/consent", {
    method: "POST",
    body: JSON.stringify({ analyticsConsent, marketingConsent }),
  });
}

// Team management
export async function fetchAllTeams(): Promise<{ teams: TeamSummary[] }> {
  return request<{ teams: TeamSummary[] }>("/teams", { method: "GET" });
}

// User management
export async function fetchUsers(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
}): Promise<UsersListResponse> {
  const usp = new URLSearchParams();
  if (params?.page) usp.set("page", params.page.toString());
  if (params?.pageSize) usp.set("pageSize", params.pageSize.toString());
  if (params?.search) usp.set("search", params.search);

  const query = usp.toString();
  return request<UsersListResponse>(`/admin/users${query ? `?${query}` : ""}`, {
    method: "GET",
  });
}

export async function fetchUserDetail(userId: string): Promise<UserDetailResponse> {
  return request<UserDetailResponse>(`/admin/users/${userId}`, { method: "GET" });
}

export async function createUser(payload: {
  email: string;
  name?: string | null;
  password: string;
  roles?: string[];
  globalRole?: string;
}): Promise<{ user: User }> {
  return request<{ user: User }>("/admin/users", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateUser(
  userId: string,
  payload: {
    name?: string | null;
    email?: string;
    password?: string;
    roles?: string[];
    globalRole?: string;
    status?: "active" | "inactive" | "suspended" | "invited" | "disabled";
  }
): Promise<{ user: User }> {
  return request<{ user: User }>(`/admin/users/${userId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteUser(userId: string): Promise<void> {
  await request(`/admin/users/${userId}`, { method: "DELETE" });
}

export async function assignUserToTeam(
  userId: string,
  teamId: string,
  role: string
): Promise<{ membership: { userId: string; teamId: string; role: string } }> {
  return request<{ membership: { userId: string; teamId: string; role: string } }>(
    `/admin/users/${userId}/teams/${teamId}`,
    {
      method: "POST",
      body: JSON.stringify({ role }),
    }
  );
}

export async function removeUserFromTeam(userId: string, teamId: string): Promise<void> {
  await request(`/admin/users/${userId}/teams/${teamId}`, { method: "DELETE" });
}

export async function updateUserTeamRole(
  userId: string,
  teamId: string,
  role: string
): Promise<{ membership: { userId: string; teamId: string; role: string } }> {
  return request<{ membership: { userId: string; teamId: string; role: string } }>(
    `/admin/users/${userId}/teams/${teamId}`,
    {
      method: "PATCH",
      body: JSON.stringify({ role }),
    }
  );
}

// ==================== Response Management ====================

export interface ResponseListItem {
  id: string;
  submittedAt: string;
  formVersionId: string;
  geoCountry: string | null;
  geoRegion: string | null;
  deviceType: string | null;
  previewData: Record<string, unknown>;
}

export interface ResponseListResponse {
  responses: ResponseListItem[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface ResponseDetailItem {
  id: string;
  formId: string;
  formVersionId: string;
  submittedAt: string;
  geoCountry: string | null;
  geoRegion: string | null;
  deviceType: string | null;
  userAgent: string | null;
  payload: Record<string, unknown>;
  calculatedOutputs: Record<string, unknown> | null;
  attachments: Array<{
    fieldId: string;
    fileName: string;
    contentType: string;
    sizeBytes: number;
    r2Key: string;
  }> | null;
}

export interface FieldSummary {
  fieldId: string;
  fieldTitle: string;
  fieldType: string;
  data: {
    type: 'choice' | 'rating' | 'number' | 'text';
    options?: Array<{ label: string; count: number; percentage: number }>;
    distribution?: Array<{ value: number; count: number }>;
    average?: number;
    min?: number;
    max?: number;
    count?: number;
    sampleResponses?: string[];
    totalResponses?: number;
  };
}

export interface ResponseSummaryResponse {
  formId: string;
  totalResponses: number;
  summaries: FieldSummary[];
}

/**
 * Fetch paginated list of responses for a form
 */
export async function fetchFormResponses(
  formId: string,
  params?: { page?: number; pageSize?: number }
): Promise<ResponseListResponse> {
  const usp = new URLSearchParams();
  if (params?.page) usp.set("page", params.page.toString());
  if (params?.pageSize) usp.set("pageSize", params.pageSize.toString());

  const query = usp.toString();
  return request<ResponseListResponse>(
    `/forms/${formId}/responses/list${query ? `?${query}` : ""}`
  );
}

/**
 * Fetch a single response's full details
 */
export async function fetchFormResponseDetail(
  formId: string,
  responseId: string
): Promise<{ response: ResponseDetailItem }> {
  return request<{ response: ResponseDetailItem }>(
    `/forms/${formId}/responses/${responseId}`
  );
}

/**
 * Fetch aggregated response summary for charts
 */
export async function fetchFormResponseSummary(
  formId: string
): Promise<ResponseSummaryResponse> {
  return request<ResponseSummaryResponse>(`/forms/${formId}/responses/summary`);
}

/**
 * Delete a form response (admin only)
 */
export async function deleteFormResponse(
  formId: string,
  responseId: string
): Promise<{ success: boolean; deletedId: string }> {
  return request<{ success: boolean; deletedId: string }>(
    `/forms/${formId}/responses/${responseId}`,
    { method: "DELETE" }
  );
}

// ==================== Custom Domain Management ====================

/**
 * List all custom domains for a team
 */
export async function fetchTeamDomains(teamId: string): Promise<CustomDomainsResponse> {
  return request<CustomDomainsResponse>(`/teams/${teamId}/domains`, { method: "GET" });
}

/**
 * Get a single domain by ID
 */
export async function fetchDomainDetail(
  teamId: string,
  domainId: string
): Promise<{ domain: CustomDomain }> {
  return request<{ domain: CustomDomain }>(`/teams/${teamId}/domains/${domainId}`, {
    method: "GET",
  });
}

/**
 * Add a new custom domain to a team
 */
export async function createDomainRequest(
  teamId: string,
  payload: { domain: string; displayName?: string | null }
): Promise<{ domain: CustomDomain }> {
  return request<{ domain: CustomDomain }>(`/teams/${teamId}/domains`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * Update domain display name or metadata
 */
export async function updateDomainRequest(
  teamId: string,
  domainId: string,
  payload: { displayName?: string | null; metadata?: unknown }
): Promise<{ domain: CustomDomain }> {
  return request<{ domain: CustomDomain }>(`/teams/${teamId}/domains/${domainId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/**
 * Delete a domain from a team
 */
export async function deleteDomainRequest(
  teamId: string,
  domainId: string
): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(`/teams/${teamId}/domains/${domainId}`, {
    method: "DELETE",
  });
}

/**
 * Trigger domain verification
 */
export async function verifyDomainRequest(
  teamId: string,
  domainId: string
): Promise<{ domain: CustomDomain }> {
  return request<{ domain: CustomDomain }>(`/teams/${teamId}/domains/${domainId}/verify`, {
    method: "POST",
  });
}

/**
 * Set a domain as the default for a team
 */
export async function setDefaultDomainRequest(
  teamId: string,
  domainId: string
): Promise<{ domain: CustomDomain }> {
  return request<{ domain: CustomDomain }>(`/teams/${teamId}/domains/${domainId}/set-default`, {
    method: "POST",
  });
}
